import React, { useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useOrdersByDepartureStatus } from 'hooks/useOrdersByDepartureStatus'
import LesaPurchaseSummary from '../../SelectPurchase/LesaPurchaseSummary/LesaPurchaseSummary'
import Action, { ActionsList } from './Action'
import { isChip, isComponent } from '../../../utils/actionsUtils'
import { arrayToMap } from '../../../../client/lib/array/arrayUtils'
import LesaPurchaseSummarySkeleton from '../../SelectPurchase/LesaPurchaseSummary/Common/LesaPurchaseSummarySkeleton'
import { ChatActions, ContentTypes, MessageMetaContent, MessageMetaPayload } from 'api/support'
import ModalContext from 'contexts/ModalContext'
import LesaOTPModal from 'supportAssistant/components/Chat/MessageMeta/LesaOTPModal'
import { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import { useOffers } from 'tripPlanner/hooks/api'
import LesaOfferSummary from 'supportAssistant/components/Offers/LesaOfferSummary'
import { TypeaheadResult } from 'api/support'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineExpandFromCornerIcon from 'components/Luxkit/Icons/line/LineExpandFromCornerIcon'

const Container = styled.div`
  display: grid;
  grid-template-rows: 1fr auto 1px;
  margin-top: ${rem(12)};
  padding-left: ${rem(32)};
  width: 100%;
`

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin-top: ${rem(12)};
`

const ScrollTo = styled.div`
  padding-bottom: ${rem(4)};
  height: 0;
`

const StyledViewMoreWrapperDiv = styled.div`
  width: ${rem(230)};
`

interface Props {
  customerOrdersData: ReturnType<typeof useOrdersByDepartureStatus>
  metaContent: MessageMetaContent[]
  payload?: MessageMetaPayload;
  handleAction: (message: string, meta: any) => void
}

const getSearchLink = (destination?: TypeaheadResult) => {
  if (!destination) {
    return '/hotels?holidayTypes=Limited+Time+LUX+Exclusive'
  }

  return `/search?destinationName=${destination.name}&destinationId=${destination.id}&adults=2&children=none&flexibleWithDate=true`
}

function MetaComponents(props: Props) {
  const { customerOrdersData } = props
  const { loading, upcoming, previous } = customerOrdersData
  const { metaContent, handleAction, payload } = props

  const showModal = useContext(ModalContext)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentOrder = useMemo(() => [...upcoming, ...previous].find(order => order.id === payload?.id_orders), [payload?.id_orders])

  const { data: offers } = useOffers(
    payload?.offersSuggestions?.offers?.map((offer) => offer.id) || [],
  )

  const scrollRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [loading, currentOrder, metaContent, upcoming, previous, offers])

  const componentsMap = useMemo(() => arrayToMap(metaContent.filter(action => isComponent(action)), a => a.type), [metaContent])
  const actionsChips = useMemo(() => metaContent.filter(isChip).toReversed(), [metaContent])

  const isListOrdersAction = !!componentsMap.get(ContentTypes.LIST_ORDERS)
  const isListOffersAction = !!componentsMap.get(ContentTypes.LIST_OFFERS)
  const isOTPModalAction = !!componentsMap.get(ContentTypes.OTP_LOGIN)

  if (isListOrdersAction && loading) {
    return <>
      <LesaPurchaseSummarySkeleton />
      <ScrollTo ref={scrollRef} />
    </>
  }

  if (isOTPModalAction) {
    showModal<AccountAccessModalResult>(<LesaOTPModal email={payload?.email as string} maskedPhone={payload?.maskedPhone as string} />).then((result) => {
      handleAction(result?.loggedIn ? 'Login successful' : 'Login failed', { key: ChatActions.LIST_DEFAULT_ACTIONS, payload })
    })
  }

  return (
    <Container>
      {currentOrder && !isListOrdersAction && <LesaPurchaseSummary actions={actionsChips} componentsMap={componentsMap} payload={payload} order={currentOrder} handleAction={handleAction} />}
      {isListOrdersAction && (
        <ItemsList>
          {upcoming.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} handleAction={handleAction} />
          ))}
          {upcoming.length === 0 && previous.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} handleAction={handleAction} isPastOrder />
          ))}
          <ScrollTo ref={scrollRef} />
        </ItemsList>
      )}
      {isListOffersAction && (
        <ItemsList>
          {offers.map((offer) => (
            <LesaOfferSummary actions={actionsChips} key={offer.id} offer={offer} handleAction={handleAction} />
          ))}
          {!!offers.length && <StyledViewMoreWrapperDiv>
            <TextButton
            target="_blank"
            to={getSearchLink(payload?.offersSuggestions?.searchDestination)}
            fit="mobile-full-width"
            size="large"
            kind="secondary"
            endIcon={<LineExpandFromCornerIcon />}
            >
              View more great offers
            </TextButton>
          </StyledViewMoreWrapperDiv>}
          <ScrollTo ref={scrollRef} />
        </ItemsList>
      )}
      {!currentOrder && <ActionsList>
        {actionsChips.map((action, index) => (
          <Action action={action} key={`${action.text}-${index}`} payload={payload} handleAction={handleAction} />
        ))}
      </ActionsList>}
      <ScrollTo ref={scrollRef} />
    </Container>
  )
}

export default MetaComponents
